import React from "react";
import { Doughnut } from "@reactchartjs/react-chart.js";
import { Box, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import stepIcon from "../../../assets/svg-icons/step.svg";
import yogaIcon from "../../../assets/svg-icons/yoga.svg";
import { compose } from "recompose";

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
    height: "100%",
    width: "100%",
  },
  chartWrapper: {
    position: "relative",
    height: 300,
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  // The overlay that sits on top of the hole in the doughnut
  centerOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none", // Allows tooltips on the chart ring to still work
  },
  icon: {
    fontSize: 40,
    color: "#0288D1", // Blue color for icon
    marginBottom: theme.spacing(0.5),
  },
  bigNumber: {
    fontWeight: "bold",
    fontSize: "2.5rem",
    color: "#09456C", // Dark Navy
    lineHeight: 1.2,
  },
  statsRow: {
    marginTop: theme.spacing(0.5),
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
  },
  labelOrange: {
    color: "#D87A46", // The orange/brown color from your image
    fontWeight: 600,
  },
  valueBlue: {
    color: "#0288D1",
    fontWeight: "bold",
  },
});

function PersonalProgressGraph({ classes, data }) {
  // Destructure with defaults
  const {
    totalDone = 0,
    totalTarget = 0,
    todayDone = 0,
    activityType,
  } = data || {};

  // Logic: If totalTarget is 0, it is a PROGRAM_CHALLENGE (or similar) without goals
  const hasTarget = totalTarget > 0;

  // Calculate Chart Data
  // If we have a target, calculate remaining. If no target, show full circle (100% done).
  const remaining = hasTarget ? Math.max(0, totalTarget - totalDone) : 0;

  // Chart.js Data Structure
  const chartData = {
    labels: ["Completed", "Remaining"],
    datasets: [
      {
        data: hasTarget ? [totalDone, remaining] : [100, 0], // If no target, show full blue ring
        backgroundColor: [
          "#1976D2", // Blue (Completed)
          "#E0E0E0", // Grey (Remaining)
        ],
        hoverBackgroundColor: ["#1565C0", "#E0E0E0"],
        borderWidth: 0,
      },
    ],
  };

  // Chart Options
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 85,
    legend: {
      display: false,
    },
    tooltips: {
      enabled: hasTarget,
    },
    hover: { mode: null },
  };

  return (
    <div className={classes.root}>
      <div className={classes.chartWrapper}>
        <Doughnut data={chartData} options={doughnutOptions} />

        {/* HTML Overlay for Center Content */}
        <div className={classes.centerOverlay}>
          {/* 1. Icon */}
          {hasTarget && (
            <img src={stepIcon} alt="Steps Icon" className={classes.icon} />
          )}

          {activityType === "PROGRAM_CHALLENGE" && (
            <img
              src={yogaIcon}
              alt="Points Icon"
              className={classes.icon}
              style={{ width: "35px", height: "auto" }}
            />
          )}

          {/* 2. Today's Count (Only if target exists / EVENT type) */}
          {hasTarget && (
            <Typography className={classes.bigNumber}>{todayDone}</Typography>
          )}

          {/* 3. Total Steps (Always Shown) */}
          <div className={classes.statsRow}>
            <span className={classes.labelOrange}>
              {" "}
              {hasTarget
                ? activityType === "STEPS"
                  ? "Total Steps"
                  : "Total Distance"
                : "Total Point"}
            </span>
            <span className={classes.valueBlue}>{totalDone}</span>
          </div>

          {/* 4. Total Goal (Only if target exists / EVENT type) */}
          {hasTarget && (
            <div className={classes.statsRow}>
              <span className={classes.labelOrange}>Total Goal</span>
              <span className={classes.valueBlue}>{totalTarget}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default compose(withStyles(styles, { withTheme: true }))(
  PersonalProgressGraph,
);
